import {
	type AssetFamilySettings,
	type AssetTypeSettings,
	type PredictiveSpendSettings,
	getPredictiveSpendSettings,
	updatePredictiveSpendSettings,
} from "@/services/predictive-spend-settings-service";
import {
	getAssetFamilies,
	getMasterAssetTypes,
	getOrganizationAssetTypes,
} from "@/services/utility-service";
import type { AssetFamily, AssetType } from "@/services/utility-service";
import {
	DownOutlined,
	InfoCircleOutlined,
	ReloadOutlined,
	SaveOutlined,
} from "@ant-design/icons";
import {
	Alert,
	Button,
	Card,
	Checkbox,
	Col,
	Collapse,
	Divider,
	Form,
	InputNumber,
	Modal,
	Row,
	Spin,
	Typography,
	message,
} from "antd";
import { useCallback, useEffect, useState } from "react";

const { Title, Text } = Typography;
const { Panel } = Collapse;

interface PredictiveSpendSettingsModalProps {
	visible: boolean;
	onClose: () => void;
	organizationId: number;
	onSettingsUpdated?: () => void;
}

interface FamilyWithTypes extends AssetFamily {
	types: AssetType[];
}

export const PredictiveSpendSettingsModal: React.FC<
	PredictiveSpendSettingsModalProps
> = ({ visible, onClose, organizationId, onSettingsUpdated }) => {
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [familiesWithTypes, setFamiliesWithTypes] = useState<FamilyWithTypes[]>(
		[],
	);
	const [settings, setSettings] = useState<PredictiveSpendSettings>({});

	const fetchData = useCallback(async () => {
		if (!organizationId) return;

		try {
			setLoading(true);

			// Fetch asset families, master types, org types, and current settings in parallel
			const [families, masterTypes, orgTypes, currentSettings] =
				await Promise.all([
					getAssetFamilies(),
					getMasterAssetTypes(),
					getOrganizationAssetTypes(true), // Include deprecated to show all types
					getPredictiveSpendSettings(organizationId),
				]);

			console.log("Fetched predictive spend settings:", currentSettings);

			// Create a map of master type ID to family ID
			const masterTypeToFamily: Record<number, number> = {};
			masterTypes.forEach((masterType) => {
				masterTypeToFamily[masterType.id] = masterType.assetFamilyId;
			});

			// Group org types by family using master type relationship
			const familiesMap: Record<number, FamilyWithTypes> = {};
			families.forEach((family) => {
				familiesMap[family.id] = { ...family, types: [] };
			});

			orgTypes.forEach((orgType) => {
				const familyId = masterTypeToFamily[orgType.masterAssetTypeId];
				if (familyId && familiesMap[familyId]) {
					familiesMap[familyId].types.push(orgType);
				}
			});

			const familiesWithTypesData = Object.values(familiesMap).sort((a, b) =>
				a.name.localeCompare(b.name),
			);

			setFamiliesWithTypes(familiesWithTypesData);
			setSettings(currentSettings);

			// Set form values from new hierarchical structure
			const formValues: any = {
				annualInflationRate: currentSettings.annualInflationRate || 0,
			};

			let hasSettings = false;

			// Populate form from new hierarchical structure
			if (currentSettings.assetFamilies) {
				familiesWithTypesData.forEach((family) => {
					const familySettings = currentSettings.assetFamilies?.[family.name];
					if (familySettings) {
						// Family-level settings
						if (familySettings.usefulLife !== undefined) {
							formValues[`family_usefulLife_${family.id}`] =
								familySettings.usefulLife;
							hasSettings = true;
						}
						if (familySettings.replacementCost !== undefined) {
							formValues[`family_replacementCost_${family.id}`] =
								familySettings.replacementCost;
							hasSettings = true;
						}
						if (familySettings.installationDeliveryFactor !== undefined) {
							formValues[`family_installFactor_${family.id}`] =
								familySettings.installationDeliveryFactor * 100; // Convert to percentage
							hasSettings = true;
						}

						// Type-level settings
						if (familySettings.assetTypes) {
							family.types.forEach((type) => {
								const typeSettings = familySettings.assetTypes?.[type.name];
								if (typeSettings) {
									if (typeSettings.usefulLife !== undefined) {
										formValues[`type_usefulLife_${type.id}`] =
											typeSettings.usefulLife;
										hasSettings = true;
									}
									if (typeSettings.replacementCost !== undefined) {
										formValues[`type_replacementCost_${type.id}`] =
											typeSettings.replacementCost;
										hasSettings = true;
									}
									if (typeSettings.installationDeliveryFactor !== undefined) {
										formValues[`type_installFactor_${type.id}`] =
											typeSettings.installationDeliveryFactor * 100;
										hasSettings = true;
									}
									if (typeSettings.extendedLifeEligible !== undefined) {
										formValues[`type_extendedLife_${type.id}`] =
											typeSettings.extendedLifeEligible;
										hasSettings = true;
									}
								}
							});
						}
					}
				});
			}
			// Fallback: populate from legacy flat structure
			else if (
				currentSettings.usefulLifeOverrides ||
				currentSettings.replacementCostOverrides
			) {
				familiesWithTypesData.forEach((family) => {
					if (currentSettings.usefulLifeOverrides?.[family.name]) {
						formValues[`family_usefulLife_${family.id}`] =
							currentSettings.usefulLifeOverrides[family.name];
						hasSettings = true;
					}
					if (currentSettings.replacementCostOverrides?.[family.name]) {
						formValues[`family_replacementCost_${family.id}`] =
							currentSettings.replacementCostOverrides[family.name];
						hasSettings = true;
					}
				});
			}

			if (
				currentSettings.annualInflationRate &&
				currentSettings.annualInflationRate > 0
			) {
				hasSettings = true;
			}

			if (!hasSettings) {
				console.log("No saved settings found, using defaults");
			}

			form.setFieldsValue(formValues);
		} catch (error: any) {
			console.error("Error fetching data:", error);
			message.error(
				error.message || "Failed to load predictive spend settings",
			);
		} finally {
			setLoading(false);
		}
	}, [organizationId, form]);

	useEffect(() => {
		if (visible) {
			fetchData();
		}
	}, [visible, fetchData]);

	const handleSave = async () => {
		if (!organizationId) return;

		try {
			setSaving(true);
			const values = await form.validateFields();

			// Build the new hierarchical settings object
			const newSettings: PredictiveSpendSettings = {
				annualInflationRate: values.annualInflationRate || 0,
				assetFamilies: {},
			};

			// Extract family and type-level settings
			familiesWithTypes.forEach((family) => {
				const familySettings: AssetFamilySettings = {};
				let hasFamilyData = false;

				// Family-level settings
				const familyUsefulLife = values[`family_usefulLife_${family.id}`];
				if (
					familyUsefulLife !== undefined &&
					familyUsefulLife !== null &&
					familyUsefulLife > 0
				) {
					familySettings.usefulLife = familyUsefulLife;
					hasFamilyData = true;
				}

				const familyReplacementCost =
					values[`family_replacementCost_${family.id}`];
				if (
					familyReplacementCost !== undefined &&
					familyReplacementCost !== null &&
					familyReplacementCost > 0
				) {
					familySettings.replacementCost = familyReplacementCost;
					hasFamilyData = true;
				}

				const familyInstallFactor = values[`family_installFactor_${family.id}`];
				if (
					familyInstallFactor !== undefined &&
					familyInstallFactor !== null &&
					familyInstallFactor >= 0
				) {
					familySettings.installationDeliveryFactor = familyInstallFactor / 100; // Convert percentage to decimal
					hasFamilyData = true;
				}

				// Type-level settings
				const typeSettings: Record<string, AssetTypeSettings> = {};
				let hasTypeData = false;

				family.types.forEach((type) => {
					const typeData: AssetTypeSettings = {};
					let hasThisTypeData = false;

					const typeUsefulLife = values[`type_usefulLife_${type.id}`];
					if (
						typeUsefulLife !== undefined &&
						typeUsefulLife !== null &&
						typeUsefulLife > 0
					) {
						typeData.usefulLife = typeUsefulLife;
						hasThisTypeData = true;
					}

					const typeReplacementCost = values[`type_replacementCost_${type.id}`];
					if (
						typeReplacementCost !== undefined &&
						typeReplacementCost !== null &&
						typeReplacementCost > 0
					) {
						typeData.replacementCost = typeReplacementCost;
						hasThisTypeData = true;
					}

					const typeInstallFactor = values[`type_installFactor_${type.id}`];
					if (
						typeInstallFactor !== undefined &&
						typeInstallFactor !== null &&
						typeInstallFactor >= 0
					) {
						typeData.installationDeliveryFactor = typeInstallFactor / 100;
						hasThisTypeData = true;
					}

					const typeExtendedLife = values[`type_extendedLife_${type.id}`];
					if (typeExtendedLife !== undefined) {
						typeData.extendedLifeEligible = typeExtendedLife;
						hasThisTypeData = true;
					}

					if (hasThisTypeData) {
						typeSettings[type.name] = typeData;
						hasTypeData = true;
					}
				});

				if (hasTypeData) {
					familySettings.assetTypes = typeSettings;
					hasFamilyData = true;
				}

				if (hasFamilyData) {
					newSettings.assetFamilies![family.name] = familySettings;
				}
			});

			await updatePredictiveSpendSettings(organizationId, newSettings);
			setSettings(newSettings);
			message.success("Predictive spend settings saved successfully");

			// Notify parent component to refresh data
			if (onSettingsUpdated) {
				onSettingsUpdated();
			}

			onClose();
		} catch (error: any) {
			console.error("Error saving settings:", error);
			message.error(
				error.message || "Failed to save predictive spend settings",
			);
		} finally {
			setSaving(false);
		}
	};

	const handleReset = () => {
		// Clear form first, then reload saved values from database
		form.resetFields();
		fetchData();
		message.info("Form reset to saved values");
	};

	const handleCancel = () => {
		form.resetFields();
		onClose();
	};

	return (
		<Modal
			title={
				<div>
					<Title level={4} style={{ margin: 0 }}>
						Predictive Spend Settings
					</Title>
					<Text type="secondary" style={{ fontSize: "14px" }}>
						Configure custom useful life, replacement costs, and inflation rates
					</Text>
				</div>
			}
			open={visible}
			onCancel={handleCancel}
			width={1000}
			footer={[
				<Button
					key="reset"
					icon={<ReloadOutlined />}
					onClick={handleReset}
					disabled={saving}
				>
					Reset
				</Button>,
				<Button key="cancel" onClick={handleCancel} disabled={saving}>
					Cancel
				</Button>,
				<Button
					key="save"
					type="primary"
					icon={<SaveOutlined />}
					onClick={handleSave}
					loading={saving}
				>
					Save Settings
				</Button>,
			]}
			style={{ top: 20 }}
		>
			{loading ? (
				<div className="flex justify-center items-center p-12">
					<Spin size="large" />
				</div>
			) : (
				<div
					style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: "8px" }}
				>
					<Alert
						message="How It Works"
						description="These settings override the default calculations for predictive spend analytics. Leave fields empty to use default values. Custom useful life is in years, replacement costs are in USD."
						type="info"
						icon={<InfoCircleOutlined />}
						showIcon
						style={{ marginBottom: 24 }}
					/>

					<Form form={form} layout="vertical">
						<Title level={5}>Annual Inflation Rate</Title>
						<Text
							type="secondary"
							style={{ display: "block", marginBottom: 16 }}
						>
							Set the annual inflation rate to adjust replacement costs over
							time (e.g., 3.5 for 3.5%)
						</Text>
						<Form.Item
							name="annualInflationRate"
							label="Annual Inflation Rate (%)"
							rules={[
								{
									type: "number",
									min: 0,
									max: 100,
									message: "Rate must be between 0 and 100",
								},
							]}
						>
							<InputNumber
								placeholder="e.g., 3.5"
								style={{ width: 200 }}
								step={0.1}
								precision={2}
							/>
						</Form.Item>

						<Divider />

						<Title level={5}>Asset Family & Type Settings</Title>
						<Text
							type="secondary"
							style={{ display: "block", marginBottom: 16 }}
						>
							Configure settings at the Family level (applies to all types) or
							expand to override specific Asset Types. Settings cascade:
							Type-specific → Family-level → System defaults.
						</Text>

						<Collapse
							expandIcon={({ isActive }) => (
								<DownOutlined rotate={isActive ? 180 : 0} />
							)}
							style={{ marginBottom: 16 }}
						>
							{familiesWithTypes.map((family) => (
								<Panel
									header={
										<div
											style={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
											}}
										>
											<Text strong>{family.name}</Text>
											<Text type="secondary" style={{ fontSize: "12px" }}>
												{family.types.length} type
												{family.types.length !== 1 ? "s" : ""}
											</Text>
										</div>
									}
									key={family.id}
								>
									{/* Family-level settings */}
									<Card
										size="small"
										style={{ backgroundColor: "#f0f5ff", marginBottom: 16 }}
										title={<Text strong>Family-Level Defaults</Text>}
									>
										<Row gutter={16}>
											<Col xs={24} sm={12} md={6}>
												<Form.Item
													name={`family_usefulLife_${family.id}`}
													label="Useful Life (years)"
													rules={[
														{
															type: "number",
															min: 1,
															max: 100,
															message: "1-100 years",
														},
													]}
												>
													<InputNumber
														placeholder="Default"
														style={{ width: "100%" }}
														min={1}
														max={100}
													/>
												</Form.Item>
											</Col>
											<Col xs={24} sm={12} md={6}>
												<Form.Item
													name={`family_replacementCost_${family.id}`}
													label="Base Cost (USD)"
													rules={[
														{
															type: "number",
															min: 0,
															message: "Must be positive",
														},
													]}
												>
													<InputNumber
														placeholder="Default"
														style={{ width: "100%" }}
														min={0}
														step={100}
														formatter={(value) =>
															`$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
														}
														parser={(value) =>
															value!.replace(/\$\s?|(,*)/g, "") as any
														}
													/>
												</Form.Item>
											</Col>
											<Col xs={24} sm={12} md={6}>
												<Form.Item
													name={`family_installFactor_${family.id}`}
													label="Install & Delivery (%)"
													rules={[
														{
															type: "number",
															min: 0,
															max: 100,
															message: "0-100%",
														},
													]}
													tooltip="Percentage markup for installation and delivery costs (e.g., 15 for 15%)"
												>
													<InputNumber
														placeholder="0"
														style={{ width: "100%" }}
														min={0}
														max={100}
														step={1}
														formatter={(value) => `${value}%`}
														parser={(value) => value!.replace("%", "") as any}
													/>
												</Form.Item>
											</Col>
										</Row>
									</Card>

									{/* Type-level settings */}
									{family.types.length > 0 && (
										<>
											<Divider orientation="left" style={{ fontSize: "14px" }}>
												Type-Specific Overrides
											</Divider>
											{family.types
												.sort((a, b) => a.name.localeCompare(b.name))
												.map((type) => (
													<Card
														key={type.id}
														size="small"
														style={{
															backgroundColor: "#fafafa",
															marginBottom: 12,
														}}
														title={<Text>{type.name}</Text>}
													>
														<Row gutter={16}>
															<Col xs={24} sm={12} md={5}>
																<Form.Item
																	name={`type_usefulLife_${type.id}`}
																	label="Useful Life (years)"
																	rules={[
																		{
																			type: "number",
																			min: 1,
																			max: 100,
																			message: "1-100 years",
																		},
																	]}
																>
																	<InputNumber
																		placeholder="Use Family"
																		style={{ width: "100%" }}
																		min={1}
																		max={100}
																	/>
																</Form.Item>
															</Col>
															<Col xs={24} sm={12} md={5}>
																<Form.Item
																	name={`type_replacementCost_${type.id}`}
																	label="Base Cost (USD)"
																	rules={[
																		{
																			type: "number",
																			min: 0,
																			message: "Must be positive",
																		},
																	]}
																>
																	<InputNumber
																		placeholder="Use Family"
																		style={{ width: "100%" }}
																		min={0}
																		step={100}
																		formatter={(value) =>
																			`$ ${value}`.replace(
																				/\B(?=(\d{3})+(?!\d))/g,
																				",",
																			)
																		}
																		parser={(value) =>
																			value!.replace(/\$\s?|(,*)/g, "") as any
																		}
																	/>
																</Form.Item>
															</Col>
															<Col xs={24} sm={12} md={5}>
																<Form.Item
																	name={`type_installFactor_${type.id}`}
																	label="Install & Delivery (%)"
																	rules={[
																		{
																			type: "number",
																			min: 0,
																			max: 100,
																			message: "0-100%",
																		},
																	]}
																>
																	<InputNumber
																		placeholder="Use Family"
																		style={{ width: "100%" }}
																		min={0}
																		max={100}
																		step={1}
																		formatter={(value) => `${value}%`}
																		parser={(value) =>
																			value!.replace("%", "") as any
																		}
																	/>
																</Form.Item>
															</Col>
															<Col xs={24} sm={12} md={5}>
																<Form.Item
																	name={`type_extendedLife_${type.id}`}
																	label="Extended Life Eligible"
																	valuePropName="checked"
																	tooltip="Allow this asset type to use Extended Replacement strategy"
																>
																	<Checkbox>Can extend life</Checkbox>
																</Form.Item>
															</Col>
														</Row>
													</Card>
												))}
										</>
									)}
								</Panel>
							))}
						</Collapse>
					</Form>
				</div>
			)}
		</Modal>
	);
};
